import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Plus, Trash2 } from 'lucide-react'
import { useMemo, useState, type SyntheticEvent, type ReactNode } from 'react'

import type {
  SalesDocumentDetail,
  SalesDocumentSummary,
} from '@aeris/contracts'
import { formatMoney, toString, ZERO_MONEY } from '@aeris/money'
import {
  amountDiscount,
  computeTotals,
  parseQuantity,
  parseUnitPrice,
  percentDiscount,
} from '@aeris/sales'
import {
  Badge,
  Button,
  cn,
  Dialog,
  DialogContent,
  DialogFooter,
  Field,
  Input,
  MoneyInput,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeaderCell,
  TableRow,
  Tabs,
  TabsList,
  TabsTrigger,
} from '@aeris/ui'

import {
  ApiError,
  createSalesDocument,
  fetchParties,
  fetchPriceLists,
  resolvePriceFor,
  updateSalesDocument,
  fetchSalespeople,
} from '../lib/api.ts'
import { useAuth } from '../lib/auth-context.ts'
import { useBalcaoAnterior } from '../lib/balcao-anterior.ts'
import {
  formatUnitPrice,
  parseTypedPrice,
  toWireAmount,
} from '../lib/price-format.ts'
import { KIND_LABELS } from '../lib/sales-labels.ts'

import { AuthorisationDialog } from './authorisation-dialog.tsx'
import { PartyForm } from './party-form.tsx'
import { PartyPicker } from './party-picker.tsx'
import { ProductPicker, type PickedProduct } from './product-picker.tsx'
import { SalesDocumentHistory } from './sales-document-history.tsx'
import { SalesPaidAmendDialog } from './sales-paid-amend-dialog.tsx'

/**
 * The document editor.
 *
 * Products are added by code or barcode — the two things an operator has in
 * front of them — and the price is RESOLVED from the pricing module and then
 * CAPTURED onto the line. Once captured it never moves again, which is the
 * invariant ADR-0017 calls the most important in the module: a document that
 * re-resolves its prices is a document whose total changes after the customer
 * agreed to it.
 *
 * The running total is computed by `computeTotals` from `@aeris/sales` — the
 * same function the server stores its answer from. There is no second
 * implementation of the rounding here, so the total on screen and the total on
 * the document cannot disagree about a centavo.
 *
 * Nothing in this file does arithmetic on a `number`, and nothing formats with
 * `Intl` — which takes one. ADR-0016.
 */

/** A line being built. Everything here is already captured. */
type DraftLine = {
  readonly key: string
  readonly variantId: string
  readonly description: string
  readonly sku: string
  readonly unit: string
  /** The wire string, at six decimals. */
  readonly unitPrice: string
  readonly priceListId: string | null
  readonly priceBasis: 'base-price' | 'quantity-tier' | 'manual'
  readonly quantity: string
}

export type SalesDocumentFormProps = {
  /** Absent means "create". */
  document?: SalesDocumentDetail | undefined
  onClose: () => void
  onSaved?: ((document: SalesDocumentSummary) => void) | undefined
  /**
   * A espécie de um documento NOVO, e travada (F170).
   *
   * A Produção abre este formulário para criar um PEDIDO: o padrão aqui é
   * venda, e uma venda criada a partir da fila de produção nunca apareceria
   * nela. Com a espécie dada, o seletor fica fixo — não é uma sugestão.
   */
  initialKind?: SalesDocumentSummary['kind'] | undefined
}

const toDraftLines = (document: SalesDocumentDetail): DraftLine[] =>
  document.lines.map((line) => ({
    key: line.id,
    variantId: line.variantId,
    description: line.description,
    sku: line.sku ?? '',
    unit: line.unit,
    unitPrice: line.unitPrice,
    priceListId: line.priceListId,
    priceBasis: line.priceBasis,
    quantity: line.quantity,
  }))

/** O motivo que vai para o histórico quando o gerente autoriza (F318). */
const AMEND_REASON = 'Alteração autorizada com senha'

export const SalesDocumentForm = ({
  document,
  onClose,
  onSaved,
  initialKind,
}: SalesDocumentFormProps): ReactNode => {
  const queryClient = useQueryClient()
  const editing = document !== undefined
  /*
   * O que se pode mudar, dito pelo servidor para quem abriu (F147).
   *
   * `header-only` é a venda paga e o pedido que já recebeu: cliente, vendedor,
   * observação e motoboy mudam, os itens e o desconto ficam só leitura. O
   * servidor recusa de qualquer jeito — a tela só não oferece o que não vai
   * funcionar.
   */
  const editScope = document?.editScope ?? 'lines'
  const linesLocked = editing && editScope === 'header-only'
  /**
   * Mexer num pedido ou venda que já saiu pede a senha do gerente (F318);
   * orçamento e rascunho não. O motivo do histórico diz quem autorizou.
   */
  const reasonRequired =
    editing && document.kind !== 'quote' && document.status !== 'draft'
  const [courierPartyId, setCourierPartyId] = useState(
    document?.courierPartyId ?? ''
  )
  const dispatched = editing && document.dispatchedAt !== null
  const [tab, setTab] = useState<'items' | 'history'>('items')
  /**
   * Whether this person may sell for something the table did not produce.
   *
   * Decides what the screen OFFERS; the rule is the server's, which re-prices
   * every line of every document and refuses a departure from a role without
   * `sales.price.override`. F026.
   */
  const { allows, session: authSession } = useAuth()
  const classic = useBalcaoAnterior()
  const canOverridePrice = allows('sales.price.override')

  const [kind, setKind] = useState<SalesDocumentSummary['kind']>(
    document?.kind ?? initialKind ?? 'sale'
  )
  const [customerPartyId, setCustomerPartyId] = useState(
    document?.customerPartyId ?? ''
  )
  /*
   * O cliente nasce junto com o pedido (F174): o atendente da fábrica atende
   * gente nova quase sempre, e sair do pedido para cadastrar em Clientes é
   * perder o pedido no meio.
   */
  const canCreateCustomer = allows('party.create')
  const [creatingCustomer, setCreatingCustomer] = useState(false)
  const [priceListId, setPriceListId] = useState(document?.priceListId ?? '')
  const [reservesStock, setReservesStock] = useState(
    document?.reservesStock ?? false
  )
  const [validUntil, setValidUntil] = useState(
    document?.validUntil?.slice(0, 10) ?? ''
  )
  /*
   * O desconto: um campo, dois modos — F100.
   *
   * Um input só, e não dois campos lado a lado: dois campos convidam a
   * preencher os dois, e o servidor recusa isso com razão (`discount_named_twice`)
   * porque adivinhar qual vale seria escolher em silêncio o desconto de uma
   * nota.
   *
   * O modo abre no que o documento GUARDA — percentual quando ele lembra de
   * um, reais quando não. É o que faz reabrir uma venda mostrar "10%" em vez
   * do valor que aquilo deu naquele dia.
   */
  const [discountMode, setDiscountMode] = useState<'amount' | 'percent'>(
    document?.discountPercent == null ? 'amount' : 'percent'
  )
  const [discount, setDiscount] = useState(() => {
    if (document === undefined) return ''
    if (document.discountPercent != null) {
      // Sem os zeros que a escala de seis casas carrega: quem digitou "10"
      // tem de ver "10", não "10,000000".
      return document.discountPercent.replace(/\.?0+$/, '').replace('.', ',')
    }
    return document.discountAmount === '0.00'
      ? ''
      : document.discountAmount.replace('.', ',')
  })
  const [notes, setNotes] = useState(document?.notes ?? '')
  /*
   * Quem vendeu (F034/F035), como no PDV. O servidor grava quem está logado
   * quando nada vem — o que apagava o vendedor de verdade de toda venda feita
   * por esta tela, quando quem digita é o caixa e não quem atendeu.
   */
  const [salespersonUserId, setSalespersonUserId] = useState(
    document?.salespersonUserId ?? authSession?.user.id ?? ''
  )
  const salespeople = useQuery({
    queryKey: ['salespeople'],
    queryFn: ({ signal }) => fetchSalespeople(signal),
  })
  /**
   * The line whose price is being typed, and what has been typed so far.
   *
   * Keyed by the line's key rather than its index: a line removed while
   * another is being edited would otherwise shift the editor onto a different
   * product, and the person typing would not notice.
   */
  const [editingPrice, setEditingPrice] = useState<string | null>(null)
  const [priceDraft, setPriceDraft] = useState('')

  const [lines, setLines] = useState<DraftLine[]>(
    document === undefined ? [] : toDraftLines(document)
  )

  const [chosen, setChosen] = useState<PickedProduct | null>(null)
  const [quantity, setQuantity] = useState('1')
  const [error, setError] = useState<string | null>(null)
  const [lookupError, setLookupError] = useState<string | null>(null)

  const customers = useQuery({
    queryKey: ['parties', { role: 'customer', system: true }],
    queryFn: ({ signal }) =>
      fetchParties(
        { role: 'customer', status: 'active', system: true, pageSize: 1 },
        signal
      ),
  })

  const priceLists = useQuery({
    queryKey: ['price-lists'],
    queryFn: ({ signal }) => fetchPriceLists(signal),
  })

  /**
   * The customer defaults to the tenant's Consumidor Final.
   *
   * The customer is never null (F013, decision 1), and a till sale to somebody
   * who did not identify themselves is the ordinary case rather than the
   * exception — so it is the default rather than a field the operator has to
   * fill before they can start.
   *
   * **Pedido, e não procurado (F104).** Isto lia os cem primeiros clientes por
   * nome e procurava `is_system` entre eles; numa conta com 8988 pessoas ele
   * não está lá, e o `?? items[0]` que vinha depois abria o formulário no nome
   * de quem ordena primeiro. Sem Consumidor Final não há padrão — e o `save`
   * abaixo já recusa cliente vazio com a frase certa.
   */
  const defaultCustomerId = useMemo(
    () => customers.data?.items[0]?.id ?? '',
    [customers.data]
  )

  const effectiveCustomerId =
    customerPartyId.length > 0 ? customerPartyId : defaultCustomerId

  /**
   * The running total, from the same function the server uses.
   *
   * Recomputed on every keystroke rather than fetched, because a total that
   * lags the cart is a total the operator stops trusting — and because the
   * shared package is what guarantees the two answers agree.
   */
  /*
   * O que foi digitado, uma vez só.
   *
   * Era `parseTypedPrice(discount)` chamado três vezes na mesma expressão —
   * duas para decidir e uma para usar. Com dois modos isso viraria seis.
   */
  const typedDiscount = useMemo(() => parseTypedPrice(discount), [discount])

  const totals = useMemo(() => {
    const outcome = computeTotals(
      lines.map((line) => ({
        quantity: parseQuantity(line.quantity) ?? ZERO_MONEY,
        unitPrice: parseUnitPrice(line.unitPrice) ?? ZERO_MONEY,
        discountAmount: ZERO_MONEY,
      })),
      typedDiscount === null
        ? ZERO_MONEY
        : discountMode === 'percent'
          ? percentDiscount(typedDiscount)
          : amountDiscount(typedDiscount)
    )

    return outcome.ok ? outcome.totals : null
  }, [lines, typedDiscount, discountMode])

  /**
   * Moves ONE line to another price list.
   *
   * The document's table is the DEFAULT new lines arrive on; this is how a
   * single item leaves it — the wholesale box in an otherwise retail order,
   * which before F026 meant either two documents or the wrong price on one.
   *
   * Refuses rather than guesses: a variant with no price in the chosen table
   * leaves the line exactly as it was and says so. Keeping the old price under
   * the new table's name would be a line that lies about where it came from.
   */
  const repriceLine = useMutation({
    mutationFn: async (input: { key: string; priceListId: string }) => {
      const line = lines.find((entry) => entry.key === input.key)
      if (line === undefined)
        throw new ApiError(404, 'line', 'Item não encontrado.')

      const resolution = await resolvePriceFor({
        variantId: line.variantId,
        quantity: line.quantity,
        ...(input.priceListId.length > 0
          ? { priceListId: input.priceListId }
          : {}),
      })

      if (!resolution.applies || resolution.unitPrice === null) {
        throw new ApiError(
          422,
          'no_price',
          `${line.description}: sem preço nessa tabela.`
        )
      }

      return { key: input.key, resolution } as const
    },
    onSuccess: ({ key, resolution }) => {
      setLookupError(null)
      setLines((current) =>
        current.map((entry) =>
          entry.key === key
            ? {
                ...entry,
                unitPrice: resolution.unitPrice ?? entry.unitPrice,
                priceListId: resolution.priceList?.id ?? null,
                // Deliberately not `manual`: choosing a table is asking for
                // that table's price, which is the opposite of overriding it.
                priceBasis: resolution.basis ?? 'base-price',
              }
            : entry
        )
      )
    },
    onError: (cause: unknown) => {
      setLookupError(
        cause instanceof ApiError
          ? cause.message
          : 'Não foi possível trocar a tabela deste item.'
      )
    },
  })

  /**
   * Prices the chosen product and captures the answer onto a line.
   *
   * It used to do the finding too, by matching the typed term against a SKU or
   * a barcode EXACTLY — so a name found nothing and the screen blamed the
   * code. `ProductPicker` now answers "which product?" while the operator
   * types, and this is left with the part that was always its own: resolve the
   * price, capture it, and refuse a product that has none.
   */
  const addLine = useMutation({
    mutationFn: async () => {
      if (chosen === null)
        throw new ApiError(422, 'empty', 'Escolha um produto.')

      const quantityValue = parseQuantity(quantity.replace(',', '.'))
      if (quantityValue === null || quantityValue.units <= 0n) {
        throw new ApiError(
          422,
          'quantity',
          'Informe uma quantidade maior que zero.'
        )
      }

      const resolution = await resolvePriceFor({
        variantId: chosen.variantId,
        quantity: toString(quantityValue),
        ...(priceListId.length > 0 ? { priceListId } : {}),
      })

      // R$ 0,00 is a giveaway somebody will sell at. "No price" is a refusal.
      if (!resolution.applies || resolution.unitPrice === null) {
        throw new ApiError(
          422,
          'no_price',
          'Este produto não tem preço na tabela escolhida.'
        )
      }

      return {
        key: `${chosen.variantId}-${String(Date.now())}`,
        variantId: chosen.variantId,
        description: chosen.productName,
        sku: chosen.sku,
        unit: chosen.unit,
        // Captured here, and never resolved again.
        unitPrice: resolution.unitPrice,
        priceListId: resolution.priceList?.id ?? null,
        priceBasis: resolution.basis ?? 'manual',
        quantity: toString(quantityValue),
      } satisfies DraftLine
    },
    onSuccess: (line) => {
      setLines((current) => [...current, line])
      setChosen(null)
      setQuantity('1')
      setLookupError(null)
    },
    onError: (cause: unknown) => {
      setLookupError(
        cause instanceof ApiError
          ? cause.message
          : 'Não foi possível adicionar o item.'
      )
    },
  })

  /*
   * A senha do gerente (F318): a vendedora altera o pedido que já saiu, com o
   * gerente autorizando no computador dela.
   */
  const [askingManager, setAskingManager] = useState(false)
  const [amendingPaid, setAmendingPaid] = useState(false)

  const save = useMutation({
    mutationFn: async (
      authorised: { id: string; reason: string } | undefined
    ) => {
      if (effectiveCustomerId.length === 0) {
        throw new ApiError(422, 'customer', 'Escolha um cliente.')
      }
      const authorisationId = authorised?.id
      const reason = authorised?.reason ?? ''

      /*
       * O motivo e o motoboy vão em TODA alteração, com ou sem itens. Antes
       * só iam no caminho travado, e o pedido entregue sem acerto — que pode
       * mexer nos itens — chegava ao servidor sem motivo e era recusado
       * (pedido 392 da Elite, 07/10).
       */
      const amendment = {
        ...(dispatched && courierPartyId !== '' ? { courierPartyId } : {}),
        ...(authorisationId === undefined ? {} : { authorisationId }),
        ...(reason.trim().length > 0
          ? {
              reason: reason.trim(),
              salespersonChangeReason: reason.trim(),
            }
          : {}),
      }

      /*
       * Venda paga e pedido com baixa: só os dados (F147). Mandar os itens,
       * mesmo iguais, seria pedir ao servidor para mexer no valor — e ele
       * recusa com razão.
       */
      if (linesLocked) {
        return await updateSalesDocument(document.id, {
          version: document.version,
          customerPartyId: effectiveCustomerId,
          notes: notes.trim().length === 0 ? null : notes.trim(),
          salespersonUserId:
            salespersonUserId === '' ? null : salespersonUserId,
          ...amendment,
        })
      }

      const payload = {
        customerPartyId: effectiveCustomerId,
        reservesStock,
        priceListId: priceListId.length > 0 ? priceListId : null,
        validUntil:
          validUntil.length === 0
            ? null
            : new Date(`${validUntil}T23:59:59`).toISOString(),
        /*
         * Um dos dois, nunca os dois. O modo escolhido manda o seu campo e
         * ZERA o outro — mandar o percentual sem apagar o valor deixaria o
         * documento com os dois preenchidos, que é o que o servidor recusa.
         */
        ...(discountMode === 'percent'
          ? {
              discountAmount: '0',
              discountPercent:
                typedDiscount === null ? null : toWireAmount(typedDiscount),
            }
          : {
              discountAmount:
                typedDiscount === null ? '0' : toWireAmount(typedDiscount),
              discountPercent: null,
            }),
        notes: notes.trim().length === 0 ? null : notes.trim(),
        salespersonUserId: salespersonUserId === '' ? null : salespersonUserId,
        lines: lines.map((line) => ({
          variantId: line.variantId,
          description: line.description,
          unit: line.unit,
          unitPrice: line.unitPrice,
          priceListId: line.priceListId,
          priceBasis: line.priceBasis,
          quantity: line.quantity,
          discountAmount: '0',
        })),
      }

      return document === undefined
        ? await createSalesDocument({ kind, ...payload })
        : await updateSalesDocument(document.id, {
            // The version this form was opened with. If somebody else saved in
            // the meantime the server refuses rather than overwriting them.
            version: document.version,
            ...payload,
            ...amendment,
          })
    },
    onSuccess: async (response) => {
      await queryClient.invalidateQueries({ queryKey: ['sales-documents'] })
      onSaved?.(response.document)
      onClose()
    },
    onError: (cause: unknown) => {
      if (
        cause instanceof ApiError &&
        cause.code === 'amendment_requires_manager' &&
        editing
      ) {
        setAskingManager(true)
      }
      setError(
        cause instanceof ApiError
          ? cause.message
          : 'Não foi possível salvar. Tente novamente.'
      )
    },
  })

  const submit = (event: SyntheticEvent): void => {
    event.preventDefault()
    setError(null)
    if (reasonRequired) {
      if (effectiveCustomerId.length === 0) {
        setError('Escolha um cliente.')
        return
      }
      setAskingManager(true)
      return
    }
    save.mutate(undefined)
  }

  return (
    <>
      <Dialog
        open
        onOpenChange={(next) => {
          if (!next) onClose()
        }}
      >
        <DialogContent
          title={
            editing
              ? `Editar ${KIND_LABELS[document.kind].toLowerCase()}${document.number === null ? '' : ` nº ${String(document.number)}`}`
              : 'Novo documento de venda'
          }
          description={
            linesLocked
              ? 'Cliente, vendedor e observação mudam. Toda alteração fica no histórico.'
              : 'Adicione produtos pelo código ou código de barras. O preço vem da tabela e fica gravado no item.'
          }
          className="max-w-3xl"
        >
          {/* O histórico ao lado do documento (F147): quem mudou, o quê e quando. */}
          {editing ? (
            <Tabs
              value={tab}
              onValueChange={(next) => {
                setTab(next === 'history' ? 'history' : 'items')
              }}
            >
              <TabsList aria-label="Documento e histórico">
                <TabsTrigger value="items">Documento</TabsTrigger>
                <TabsTrigger value="history">Histórico</TabsTrigger>
              </TabsList>
            </Tabs>
          ) : null}

          {editing && tab === 'history' ? (
            <SalesDocumentHistory documentId={document.id} />
          ) : null}

          <form
            onSubmit={submit}
            hidden={editing && tab === 'history'}
            className="flex flex-col gap-4"
          >
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Tipo">
                {(field) => (
                  <Select
                    {...field}
                    value={kind}
                    // The kind decides which transitions the document has, so
                    // changing it after creation would move a document into a
                    // workflow its current status does not exist in.
                    disabled={editing || initialKind !== undefined}
                    onChange={(event) => {
                      setKind(
                        event.target.value as SalesDocumentSummary['kind']
                      )
                    }}
                  >
                    {(['sale', 'order', 'quote'] as const).map((value) => (
                      <option key={value} value={value}>
                        {KIND_LABELS[value]}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>

              <Field
                label="Cliente"
                // F361: o código do cliente à vista, como a loja o conhece.
                {...(!classic &&
                document?.customerContact?.code != null &&
                effectiveCustomerId === document.customerPartyId
                  ? { hint: `Cód. ${document.customerContact.code}` }
                  : {})}
              >
                {(field) => (
                  <div className="flex items-start gap-2">
                    <PartyPicker
                      {...field}
                      className="min-w-0 flex-1"
                      value={effectiveCustomerId}
                      onChange={setCustomerPartyId}
                      role="customer"
                    />
                    {canCreateCustomer ? (
                      <Button
                        type="button"
                        variant="secondary"
                        onClick={() => {
                          setCreatingCustomer(true)
                        }}
                      >
                        <Plus aria-hidden="true" className="h-4 w-4" />
                        Cliente novo
                      </Button>
                    ) : null}
                  </div>
                )}
              </Field>

              <Field label="Vendedor" hint="Quem atendeu o cliente">
                {(field) => (
                  <Select
                    {...field}
                    value={salespersonUserId}
                    onChange={(event) => {
                      setSalespersonUserId(event.target.value)
                    }}
                  >
                    {(salespeople.data?.items ?? []).map((person) => (
                      <option key={person.userId} value={person.userId}>
                        {person.displayName}
                      </option>
                    ))}
                    {salespeople.data?.items.some(
                      (person) => person.userId === salespersonUserId
                    ) === true || salespersonUserId === '' ? null : (
                      <option value={salespersonUserId}>
                        {document?.salespersonName ??
                          authSession?.user.displayName ??
                          'Quem está logado'}
                      </option>
                    )}
                  </Select>
                )}
              </Field>

              <Field label="Tabela de preço">
                {(field) => (
                  <Select
                    {...field}
                    value={priceListId}
                    onChange={(event) => {
                      setPriceListId(event.target.value)
                    }}
                  >
                    <option value="">Padrão da conta</option>
                    {(priceLists.data?.priceLists ?? []).map((list) => (
                      <option key={list.id} value={list.id}>
                        {list.name}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>

              <Field
                label="Válido até"
                hint="Prazo da proposta. Depois dele o preço gravado deixa de valer."
              >
                {(field) => (
                  <Input
                    {...field}
                    type="date"
                    value={validUntil}
                    onChange={(event) => {
                      setValidUntil(event.target.value)
                    }}
                  />
                )}
              </Field>
            </div>

            {/* Explicit, and not implied by the type of document: a shop quoting
              the last unit may want to hold it, and a wholesaler taking a
              sixty-day order may not. */}
            {/* Só no rascunho (F147): num orçamento enviado a reserva já existe,
              ou não, e ela acompanha os itens sozinha. */}
            {!editing || document.status === 'draft' ? (
              <label className="flex items-center gap-2 text-(length:--text-sm) text-(--text-secondary)">
                <input
                  type="checkbox"
                  checked={reservesStock}
                  onChange={(event) => {
                    setReservesStock(event.target.checked)
                  }}
                />
                Reservar estoque ao confirmar
              </label>
            ) : null}

            {linesLocked ? (
              <div
                role="note"
                className="flex flex-wrap items-center justify-between gap-3 rounded-(--radius-md) border border-(--border-subtle) p-3 text-(length:--text-sm) text-(--text-secondary)"
              >
                <span>
                  {document.kind === 'sale'
                    ? 'Esta venda já foi paga: os itens não mudam aqui.'
                    : 'Este pedido já foi acertado: os itens não mudam aqui.'}{' '}
                  Para tirar uma peça, use Corrigir itens — o Aeris acerta o
                  título, o estoque e o vale (F348).
                </span>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    setAmendingPaid(true)
                  }}
                >
                  Corrigir itens
                </Button>
              </div>
            ) : null}

            <div
              hidden={linesLocked}
              className="flex flex-wrap items-end gap-2 rounded-(--radius-md) border border-(--border-subtle) p-3"
            >
              <Field label="Produto" className="min-w-56 flex-1">
                {(field) => (
                  <ProductPicker
                    {...field}
                    value={chosen}
                    onChange={setChosen}
                  />
                )}
              </Field>

              <Field label="Quantidade" className="w-32">
                {(field) => (
                  <Input
                    {...field}
                    value={quantity}
                    inputMode="decimal"
                    onChange={(event) => {
                      setQuantity(event.target.value)
                    }}
                  />
                )}
              </Field>

              <Button
                type="button"
                variant="secondary"
                size="sm"
                disabled={addLine.isPending}
                leadingIcon={<Plus aria-hidden="true" className="h-4 w-4" />}
                onClick={() => {
                  addLine.mutate()
                }}
              >
                Adicionar item
              </Button>
            </div>

            {lookupError === null ? null : (
              <p
                role="alert"
                className="text-(length:--text-sm) text-(--danger)"
              >
                {lookupError}
              </p>
            )}

            {lines.length === 0 ? (
              <p className="text-(length:--text-sm) text-(--text-muted)">
                Nenhum item ainda.
              </p>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableHeaderCell>Produto</TableHeaderCell>
                      <TableHeaderCell>Un.</TableHeaderCell>
                      <TableHeaderCell className="text-right">
                        Qtd.
                      </TableHeaderCell>
                      <TableHeaderCell className="text-right">
                        Preço
                      </TableHeaderCell>
                      <TableHeaderCell className="text-right">
                        Total
                      </TableHeaderCell>
                      <TableHeaderCell className="text-right">
                        <span className="sr-only">Ações</span>
                      </TableHeaderCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {lines.map((line, index) => (
                      <TableRow key={line.key}>
                        <TableCell>
                          {line.description}
                          {/* O código da peça, para conferir (24/10). */}
                          {line.sku === '' ? null : (
                            <span className="ml-2 font-(--font-mono) text-(length:--text-xs) text-(--text-muted)">
                              {line.sku}
                            </span>
                          )}
                          {/*
                          Which table this price came from, and the way to move
                          it. Under the name rather than in a column of its own:
                          it is an answer about the price, read far less often
                          than it is ignored.
                        */}
                          <select
                            aria-label={`Tabela de preço de ${line.description}`}
                            value={line.priceListId ?? ''}
                            disabled={repriceLine.isPending || linesLocked}
                            onChange={(event) => {
                              repriceLine.mutate({
                                key: line.key,
                                priceListId: event.target.value,
                              })
                            }}
                            className={cn(
                              'mt-0.5 block max-w-full rounded-(--radius-sm) border-none bg-transparent',
                              'px-0 py-0 text-(length:--text-xs) text-(--text-muted)',
                              'hover:text-(--text-secondary) focus-visible:outline-none',
                              'focus-visible:ring-2 focus-visible:ring-(--border-focus)'
                            )}
                          >
                            {/*
                            The document's default is not offered once the line
                            has a real list: naming the list that priced it is
                            what makes the row honest.
                          */}
                            {line.priceListId === null ? (
                              <option value="">Tabela do documento</option>
                            ) : null}
                            {(priceLists.data?.priceLists ?? []).map((list) => (
                              <option key={list.id} value={list.id}>
                                {list.name}
                              </option>
                            ))}
                          </select>
                        </TableCell>
                        <TableCell>{line.unit}</TableCell>
                        <TableCell className="text-right font-(--font-mono)">
                          {line.quantity.replace(/\.?0+$/, '')}
                        </TableCell>
                        <TableCell className="text-right font-(--font-mono)">
                          {editingPrice === line.key ? (
                            <input
                              autoFocus
                              inputMode="decimal"
                              aria-label={`Preço de ${line.description}`}
                              value={priceDraft}
                              onChange={(event) => {
                                setPriceDraft(event.target.value)
                              }}
                              onBlur={() => {
                                setEditingPrice(null)
                              }}
                              onKeyDown={(event) => {
                                if (event.key === 'Enter') {
                                  event.preventDefault()
                                  const next = parseTypedPrice(priceDraft)
                                  // An unreadable draft leaves the price alone.
                                  // Zeroing it would be a giveaway nobody typed.
                                  if (next !== null) {
                                    setLines((current) =>
                                      current.map((entry) =>
                                        entry.key === line.key
                                          ? {
                                              ...entry,
                                              unitPrice: toWireAmount(next),
                                              priceBasis: 'manual',
                                            }
                                          : entry
                                      )
                                    )
                                  }
                                  setEditingPrice(null)
                                  return
                                }
                                if (event.key === 'Escape') {
                                  event.preventDefault()
                                  event.stopPropagation()
                                  setEditingPrice(null)
                                }
                              }}
                              className={cn(
                                'w-24 rounded-(--radius-sm) border border-(--border-focus)',
                                'bg-(--surface) px-1 py-0.5 text-right',
                                'font-(--font-mono) text-(length:--text-sm) outline-none'
                              )}
                            />
                          ) : (
                            <button
                              type="button"
                              // Not a button when the operator may not depart:
                              // an affordance that always refuses teaches people
                              // to distrust the screen. The rule itself is the
                              // server's — it re-prices every line.
                              disabled={!canOverridePrice || linesLocked}
                              // Named for what it DOES. Without this the button's
                              // accessible name is the price itself, which reads
                              // as a value rather than as something to press.
                              aria-label={`Alterar o preço de ${line.description}`}
                              title={
                                canOverridePrice
                                  ? 'Alterar o preço deste item'
                                  : 'Você não tem permissão para alterar o preço'
                              }
                              className={cn(
                                'font-(--font-mono) tabular-nums',
                                canOverridePrice &&
                                  'rounded-(--radius-sm) px-1 underline decoration-dotted underline-offset-4 hover:bg-(--surface-raised)'
                              )}
                              onClick={() => {
                                setEditingPrice(line.key)
                                setPriceDraft(line.unitPrice)
                              }}
                            >
                              {formatUnitPrice(line.unitPrice)}
                            </button>
                          )}
                          {line.priceBasis === 'manual' ? (
                            <Badge tone="warning" className="ml-2">
                              preço alterado
                            </Badge>
                          ) : null}
                        </TableCell>
                        <TableCell className="text-right font-(--font-mono)">
                          {formatMoney(
                            totals?.lines[index]?.lineTotal ?? ZERO_MONEY
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            hidden={linesLocked}
                            aria-label={`Remover ${line.description}`}
                            onClick={() => {
                              setLines((current) =>
                                current.filter(
                                  (entry) => entry.key !== line.key
                                )
                              )
                            }}
                            leadingIcon={
                              <Trash2
                                aria-hidden="true"
                                className="h-3.5 w-3.5"
                              />
                            }
                          >
                            Remover
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            <div className="flex flex-wrap items-end justify-between gap-3">
              <div className="flex flex-col gap-1">
                <Field
                  label="Desconto no documento"
                  hint={
                    discountMode === 'percent'
                      ? 'Reajusta sozinho quando os itens mudam.'
                      : 'Distribuído entre os itens, para que a soma bata com o total.'
                  }
                  className="w-56"
                >
                  {(field) => (
                    <MoneyInput
                      {...field}
                      value={discount}
                      disabled={linesLocked}
                      onValueChange={setDiscount}
                    />
                  )}
                </Field>

                {/*
                Dois botões sobre UM campo, e não dois campos (F100).
                Trocar de modo não apaga o que foi digitado: quem escreveu 10
                pensando em reais e percebe que queria por cento não deve ter
                de digitar de novo.
              */}
                <div
                  role="group"
                  aria-label="Desconto em reais ou percentual"
                  className="flex gap-1"
                >
                  {(
                    [
                      ['amount', 'R$'],
                      ['percent', '%'],
                    ] as const
                  ).map(([mode, label]) => (
                    <button
                      key={mode}
                      type="button"
                      aria-pressed={discountMode === mode}
                      onClick={() => {
                        setDiscountMode(mode)
                      }}
                      className={`rounded-(--radius-sm) border px-2 py-0.5 text-(length:--text-xs) ${
                        discountMode === mode
                          ? 'border-(--brand-primary) text-(--brand-primary)'
                          : 'border-(--border-default) text-(--text-muted)'
                      }`}
                    >
                      {label}
                    </button>
                  ))}

                  {/*
                  O valor que o percentual dá, em cinza ao lado: a caixa
                  precisa ver o desconto em reais antes de salvar, porque é
                  esse número que ela vai dizer ao cliente.
                */}
                  {discountMode === 'percent' && totals !== null ? (
                    <span className="self-center pl-1 text-(length:--text-xs) text-(--text-muted)">
                      = {formatMoney(totals.discount)}
                    </span>
                  ) : null}
                </div>
              </div>

              <div className="flex flex-col items-end gap-1">
                <span className="text-(length:--text-xs) text-(--text-muted)">
                  Total
                </span>
                {/* Announced, so somebody using a screen reader hears the total
                  change as items are scanned rather than only after saving. */}
                <output
                  aria-live="polite"
                  className="font-(--font-mono) text-(length:--text-xl) text-(--text-primary)"
                >
                  {formatMoney(totals?.total ?? ZERO_MONEY)}
                </output>
                {totals === null ? (
                  <Badge tone="danger">Desconto maior que o total</Badge>
                ) : null}
              </div>
            </div>

            <Field label="Observações">
              {(field) => (
                <Input
                  {...field}
                  value={notes}
                  onChange={(event) => {
                    setNotes(event.target.value)
                  }}
                />
              )}
            </Field>

            {dispatched ? (
              <Field label="Motoboy" hint="Quem levou a entrega">
                {(field) => (
                  <PartyPicker
                    {...field}
                    value={courierPartyId}
                    onChange={setCourierPartyId}
                    role="courier"
                  />
                )}
              </Field>
            ) : null}

            {error === null ? null : (
              <p
                role="alert"
                className="text-(length:--text-sm) text-(--danger)"
              >
                {error}
              </p>
            )}

            <DialogFooter>
              <Button type="button" variant="secondary" onClick={onClose}>
                Cancelar
              </Button>
              <Button
                type="submit"
                variant="primary"
                disabled={save.isPending || totals === null}
              >
                {editing ? 'Salvar' : 'Cadastrar'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {amendingPaid && editing ? (
        <SalesPaidAmendDialog
          document={document}
          onClose={() => {
            setAmendingPaid(false)
          }}
          onDone={() => {
            setAmendingPaid(false)
            onClose()
          }}
        />
      ) : null}

      {askingManager && editing ? (
        <AuthorisationDialog
          title={`Alterar ${KIND_LABELS[document.kind].toLowerCase()}${document.number === null ? '' : ` nº ${String(document.number)}`}`}
          description="Este documento já saiu. Digite o e-mail e a senha do gerente, do admin ou do dono para salvar a alteração."
          action="sales.amend"
          targetType="sales_document"
          targetId={document.id}
          fixedReason={AMEND_REASON}
          onAuthorised={(authorisationId, _reason, authorisedBy) => {
            setAskingManager(false)
            setError(null)
            save.mutate({
              id: authorisationId,
              reason: `${AMEND_REASON} por ${authorisedBy}`,
            })
          }}
          onClose={() => {
            setAskingManager(false)
          }}
        />
      ) : null}

      {creatingCustomer ? (
        <PartyForm
          onClose={() => {
            setCreatingCustomer(false)
          }}
          onCreated={(party) => {
            setCustomerPartyId(party.id)
          }}
        />
      ) : null}
    </>
  )
}
